/**
 * Admin → Country Management
 *
 * 3-state lifecycle per country (super-admin only):
 *   - active       (green)  → fully open
 *   - maintenance  (yellow) → existing users can log in, new signups blocked
 *   - disabled     (red)    → registration, login, listings all off
 *
 * Every state change requires:
 *   - Reason (≥ 10 chars)
 *   - Double-confirmation (operator must type the country code)
 *   - Writes an immutable audit-log entry
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, Shield, ShieldAlert, ShieldX, Lock, AlertTriangle,
  History, X, CheckCircle2, Clock, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  { code: 'uae', name: 'United Arab Emirates', flag: '🇦🇪' },
  { code: 'india', name: 'India', flag: '🇮🇳' },
];

const STATUS_META = {
  active: {
    label: 'ACTIVE',
    color: 'emerald',
    ring: 'ring-emerald-500/40 bg-emerald-500/10 text-emerald-300',
    dot: 'bg-emerald-400',
    Icon: Shield,
    description: [
      'Public website visible',
      'Registration enabled',
      'Login enabled',
      'Concierge active',
      'Listings active',
    ],
  },
  maintenance: {
    label: 'MAINTENANCE',
    color: 'amber',
    ring: 'ring-amber-500/40 bg-amber-500/10 text-amber-300',
    dot: 'bg-amber-400',
    Icon: ShieldAlert,
    description: [
      'Existing users can log in',
      'New registrations disabled',
      'Public browsing disabled',
      'Maintenance message displayed',
      'Admin access remains available',
    ],
  },
  disabled: {
    label: 'DISABLED',
    color: 'red',
    ring: 'ring-red-500/40 bg-red-500/10 text-red-300',
    dot: 'bg-red-400',
    Icon: ShieldX,
    description: [
      'Website hidden',
      'Registration disabled',
      'Login disabled',
      'APIs disabled',
      'Search engine indexing disabled',
      'Historical data preserved',
    ],
  },
};

const ALL_STATUSES = ['active', 'maintenance', 'disabled'];

const StatusBadge = ({ status, size = 'md' }) => {
  const meta = STATUS_META[status] || STATUS_META.active;
  const sizes = size === 'lg'
    ? 'px-4 py-2 text-sm'
    : 'px-2.5 py-1 text-xs';
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full font-semibold tracking-[0.15em] uppercase ring-1 ${meta.ring} ${sizes}`}
      data-testid={`status-badge-${status}`}
    >
      <span className={`w-2 h-2 rounded-full ${meta.dot} animate-pulse`} />
      {meta.label}
    </span>
  );
};

const CountryStatusCard = ({ country, isSuperAdmin, onChange, onAudit }) => {
  const meta = STATUS_META[country.status] || STATUS_META.active;
  const updatedAt = country.updated_at
    ? new Date(country.updated_at).toLocaleString()
    : '—';

  return (
    <div
      className="glass-card p-6 border border-white/5 hover:border-white/10 transition-all"
      data-testid={`country-card-${country.code}`}
    >
      <div className="flex items-start justify-between mb-5 gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-3xl flex-shrink-0">{country.flag}</span>
          <div className="min-w-0">
            <h3 className="font-semibold text-lg truncate">{country.name}</h3>
            <span className="text-text-secondary text-[10px] uppercase tracking-[0.2em]">
              {country.code}
            </span>
          </div>
        </div>
        <StatusBadge status={country.status} size="lg" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-text-secondary">
            Active users
          </div>
          <div className="text-lg font-semibold text-white mt-0.5" data-testid={`stat-users-${country.code}`}>
            {country.active_users ?? '—'}
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-text-secondary">
            Active partners
          </div>
          <div className="text-lg font-semibold text-white mt-0.5" data-testid={`stat-partners-${country.code}`}>
            {country.active_partners ?? '—'}
          </div>
        </div>
      </div>

      {/* Last updated */}
      <div className="flex items-center gap-2 text-xs text-text-secondary mb-5">
        <Clock className="w-3.5 h-3.5" />
        <span>
          Last updated <span className="text-white/80">{updatedAt}</span>
          {country.updated_by_email && (
            <> by <span className="text-gold/80">{country.updated_by_email}</span></>
          )}
        </span>
      </div>

      {/* Public-facing status message */}
      {country.status_message && (
        <div className="mb-5 rounded-lg border border-white/10 bg-white/[0.02] p-3">
          <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">
            Public message
          </div>
          <p className="text-sm text-white/80 italic">&ldquo;{country.status_message}&rdquo;</p>
        </div>
      )}

      {/* Behavioural summary */}
      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-2">
          Current behaviour
        </div>
        <ul className="space-y-1.5">
          {meta.description.map((line) => (
            <li key={line} className="flex items-start gap-2 text-xs text-white/70">
              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 opacity-60" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-4 border-t border-white/10">
        <Button
          onClick={() => onChange(country)}
          disabled={!isSuperAdmin}
          className="flex-1 bg-gold text-black hover:bg-gold/90 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`change-status-btn-${country.code}`}
        >
          {isSuperAdmin ? 'Change Status' : <><Lock className="w-3.5 h-3.5 mr-2" /> Super Admin Only</>}
        </Button>
        <Button
          variant="ghost"
          onClick={() => onAudit(country)}
          className="border border-white/10 hover:bg-white/5"
          data-testid={`audit-btn-${country.code}`}
        >
          <History className="w-4 h-4 mr-2" /> Audit Log
        </Button>
      </div>
    </div>
  );
};

const ChangeStatusModal = ({ country, onClose, onSaved }) => {
  const [newStatus, setNewStatus] = useState(country.status);
  const [reason, setReason] = useState('');
  const [statusMessage, setStatusMessage] = useState(country.status_message || '');
  const [confirmText, setConfirmText] = useState('');
  const [step, setStep] = useState(1); // 1 = form, 2 = double-confirm
  const [saving, setSaving] = useState(false);

  const isDestructive = newStatus === 'disabled' || (country.status === 'active' && newStatus !== 'active');
  const noChange = newStatus === country.status;

  const handleSubmit = async () => {
    if (reason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    setStep(2);
  };

  const handleConfirm = async () => {
    if (confirmText.trim().toLowerCase() !== country.code) {
      toast.error(`Please type "${country.code}" to confirm`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/countries/${country.code}/status`, {
        status: newStatus,
        reason: reason.trim(),
        status_message: statusMessage.trim(),
        confirm: country.code,
      });
      toast.success(`${country.name}: ${data.prev_status} → ${data.status}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to change status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="change-status-modal"
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 10 }}
        className="bg-[#0e0e0e] border border-white/10 rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{country.flag}</span>
            <div>
              <h3 className="font-semibold text-lg">{country.name}</h3>
              <p className="text-xs text-text-secondary">Change country status</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-lg" data-testid="modal-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 1 && (
          <div className="p-5 space-y-5">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-text-secondary">Current:</span>
              <StatusBadge status={country.status} />
              <span className="text-text-secondary mx-1">→</span>
              <StatusBadge status={newStatus} />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">New status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-white/5 border-white/10" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  {ALL_STATUSES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>
                      {STATUS_META[s].label} — {STATUS_META[s].description[0]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">
                Reason (required, min 10 chars)
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why are you changing this status? This is recorded permanently."
                className="bg-white/5 border-white/10 min-h-[80px]"
                data-testid="reason-input"
              />
              <p className="text-[10px] text-text-secondary mt-1">
                {reason.length}/10 minimum characters
              </p>
            </div>

            {newStatus !== 'active' && (
              <div>
                <Label className="text-xs uppercase tracking-wider mb-2 block">
                  Public message (optional)
                </Label>
                <Input
                  value={statusMessage}
                  onChange={(e) => setStatusMessage(e.target.value)}
                  placeholder="e.g. We're upgrading the experience. Back shortly."
                  className="bg-white/5 border-white/10"
                  data-testid="status-message-input"
                />
              </div>
            )}

            {isDestructive && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-200/90">
                  This change will affect public access to the {country.name} portal.
                  All historical data is preserved.
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="ghost" onClick={onClose} className="flex-1 border border-white/10" data-testid="cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={noChange || reason.trim().length < 10}
                className="flex-1 bg-gold text-black hover:bg-gold/90 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="continue-btn"
              >
                Continue →
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="p-5 space-y-5">
            <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-red-200 mb-1">Are you absolutely sure?</p>
                <p className="text-red-200/80">
                  Are you sure you want to change <strong>{country.name}</strong>&apos;s status from{' '}
                  <strong>{STATUS_META[country.status].label}</strong> to{' '}
                  <strong>{STATUS_META[newStatus].label}</strong>?
                  Public access will be affected immediately while all existing data is preserved.
                </p>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">
                Type <code className="text-gold">{country.code}</code> to confirm
              </Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={country.code}
                className="bg-white/5 border-white/10 font-mono"
                autoFocus
                data-testid="confirm-text-input"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="ghost" onClick={() => setStep(1)} className="flex-1 border border-white/10" data-testid="back-btn">
                ← Back
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={saving || confirmText.trim().toLowerCase() !== country.code}
                className="flex-1 bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="confirm-status-change-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirm Change
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

const AuditLogModal = ({ country, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/countries/${country.code}/audit`);
        setEntries(data.entries || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load audit log');
      } finally {
        setLoading(false);
      }
    })();
  }, [country.code]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="audit-log-modal"
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
        className="bg-[#0e0e0e] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-[#0e0e0e]">
          <div className="flex items-center gap-3">
            <History className="w-5 h-5 text-gold" />
            <div>
              <h3 className="font-semibold text-lg">{country.name} — Audit Log</h3>
              <p className="text-xs text-text-secondary">Every status change is recorded permanently</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {loading && (
            <div className="flex items-center justify-center py-12 text-text-secondary">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading audit history…
            </div>
          )}
          {!loading && entries.length === 0 && (
            <div className="text-center py-12 text-text-secondary text-sm">
              No status changes recorded yet.
            </div>
          )}
          {!loading && entries.length > 0 && (
            <ol className="space-y-3" data-testid="audit-entries">
              {entries.map((e, i) => (
                <li
                  key={`${e.changed_at}-${i}`}
                  className="rounded-lg border border-white/5 bg-white/[0.02] p-4"
                  data-testid={`audit-entry-${i}`}
                >
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <StatusBadge status={e.prev_status} />
                    <span className="text-text-secondary">→</span>
                    <StatusBadge status={e.new_status} />
                    <span className="ml-auto text-[11px] text-text-secondary">
                      {new Date(e.changed_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-white/85 italic mb-2">&ldquo;{e.reason}&rdquo;</p>
                  <div className="text-[11px] text-text-secondary flex flex-wrap gap-3">
                    <span>by <span className="text-gold/80">{e.changed_by_email || e.changed_by}</span></span>
                    {e.ip && <span>IP: {e.ip}</span>}
                  </div>
                  {e.status_message && (
                    <p className="text-xs text-white/60 mt-2">
                      Public message set: &ldquo;{e.status_message}&rdquo;
                    </p>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export const AdminCountryStatusTab = ({ isSuperAdmin }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [auditing, setAuditing] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const fetched = await Promise.all(
        COUNTRIES.map(async (c) => {
          const [statusRes, statsRes] = await Promise.all([
            axios.get(`${API}/countries/${c.code}/status`).catch(() => ({ data: { status: 'active' } })),
            axios.get(`${API}/countries/${c.code}/analytics/summary`).catch(() => ({ data: {} })),
          ]);
          return {
            ...c,
            ...statusRes.data,
            active_users: statsRes.data?.users,
            // Partner count not aggregated yet — wire up once partner analytics endpoint lands
            active_partners: undefined,
          };
        })
      );
      setData(fetched);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-country-status-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-playfair text-2xl font-semibold flex items-center gap-3">
            <Globe className="w-6 h-6 text-gold" /> Country Management
          </h2>
          <p className="text-text-secondary text-sm mt-1">
            Control public access per country. Every change is double-confirmed and audit-logged.
          </p>
        </div>
        {!isSuperAdmin && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs">
            <Lock className="w-3.5 h-3.5" />
            Read-only — Super Admin role required to make changes
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-text-secondary">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading countries…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {data.map((c) => (
            <CountryStatusCard
              key={c.code}
              country={c}
              isSuperAdmin={isSuperAdmin}
              onChange={(country) => setEditing(country)}
              onAudit={(country) => setAuditing(country)}
            />
          ))}
        </div>
      )}

      <AnimatePresence>
        {editing && (
          <ChangeStatusModal
            country={editing}
            onClose={() => setEditing(null)}
            onSaved={fetchAll}
          />
        )}
        {auditing && (
          <AuditLogModal country={auditing} onClose={() => setAuditing(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminCountryStatusTab;
